// Essential polyfills for older browsers only - conditionally loaded

// Detect if we need polyfills by checking for modern browser features
const needsPolyfills = !window.fetch || !Promise.resolve || !Array.from || !Object.assign;

// Only load polyfills if browser lacks modern features
if (needsPolyfills) {
  // Array.from polyfill for IE11
  if (!Array.from) {
    Array.from = function (object: any) {
      return [].slice.call(object);
    };
  }

  // Object.assign polyfill for IE11
  if (!Object.assign) {
    Object.assign = function (target: any, ...sources: any[]) {
      if (target == null) {
        throw new TypeError('Cannot convert undefined or null to object');
      }
      const to = Object(target);
      sources.forEach(source => {
        if (source != null) {
          Object.keys(source).forEach(key => {
            to[key] = source[key];
          });
        }
      });
      return to;
    };
  }

  // String.prototype.includes polyfill for IE11
  if (!String.prototype.includes) {
    String.prototype.includes = function (search: string, start?: number) {
      if (typeof start !== 'number') {
        start = 0;
      }
      return this.indexOf(search, start) !== -1;
    };
  }

  // Array.prototype.includes polyfill for IE11
  if (!Array.prototype.includes) {
    Array.prototype.includes = function (searchElement: any, fromIndex?: number) {
      return this.indexOf(searchElement, fromIndex) !== -1;
    };
  }
}

// Element.matches polyfill for IE11
if (!Element.prototype.matches) {
  Element.prototype.matches = 
    (Element.prototype as any).msMatchesSelector || 
    Element.prototype.webkitMatchesSelector;
}

// Element.closest polyfill for IE11
if (!Element.prototype.closest) {
  Element.prototype.closest = function (selector: string) {
    let element = this as Element;
    while (element && element.nodeType === 1) {
      if (element.matches(selector)) {
        return element;
      }
      element = element.parentElement!;
    }
    return null;
  };
}

// Custom event polyfill for IE11
if (!window.CustomEvent || typeof window.CustomEvent !== 'function') {
  function CustomEvent(event: string, params?: any) {
    params = params || { bubbles: false, cancelable: false, detail: null };
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
    return evt;
  }
  window.CustomEvent = CustomEvent as any;
}

// Simple IntersectionObserver fallback (basic functionality)
if (!('IntersectionObserver' in window)) {
  class IntersectionObserverPolyfill {
    private callback: IntersectionObserverCallback;
    private options: IntersectionObserverInit;
    private elements: Element[] = [];

    constructor(callback: IntersectionObserverCallback, options: IntersectionObserverInit = {}) {
      this.callback = callback;
      this.options = options;
    }

    observe(element: Element) {
      this.elements.push(element);
      // Immediately trigger callback for simple fallback
      setTimeout(() => {
        // Cache the bounding rect to avoid multiple forced reflows
        const boundingRect = element.getBoundingClientRect();
        const entry = {
          target: element,
          isIntersecting: true,
          intersectionRatio: 1,
          boundingClientRect: boundingRect,
          intersectionRect: boundingRect,
          rootBounds: null,
          time: Date.now()
        } as IntersectionObserverEntry;
        
        this.callback([entry], this as any);
      }, 100);
    }

    unobserve(element: Element) {
      const index = this.elements.indexOf(element);
      if (index > -1) {
        this.elements.splice(index, 1);
      }
    }

    disconnect() {
      this.elements = [];
    }
  }

  (window as any).IntersectionObserver = IntersectionObserverPolyfill;
}

// Simple ResizeObserver fallback
if (!('ResizeObserver' in window)) {
  class ResizeObserverPolyfill {
    private callback: ResizeObserverCallback;
    private elements: Element[] = [];
    private rafId: number | null = null;

    constructor(callback: ResizeObserverCallback) {
      this.callback = callback;
    }

    observe(element: Element) {
      this.elements.push(element);
      this.startObserving();
    }

    unobserve(element: Element) {
      const index = this.elements.indexOf(element);
      if (index > -1) {
        this.elements.splice(index, 1);
      }
      if (this.elements.length === 0) {
        this.stopObserving();
      }
    }

    disconnect() {
      this.elements = [];
      this.stopObserving();
    }

    private startObserving() {
      if (this.rafId) return;
      
      const check = () => {
        // Batch all DOM reads first to minimize forced reflows
        const entries = this.elements.map(element => {
          // Cache the bounding rect to avoid multiple forced reflows per element
          const rect = element.getBoundingClientRect();
          return {
            target: element,
            contentRect: rect,
            borderBoxSize: [{ 
              inlineSize: rect.width,
              blockSize: rect.height
            }],
            contentBoxSize: [{ 
              inlineSize: rect.width,
              blockSize: rect.height
            }],
            devicePixelContentBoxSize: [{ 
              inlineSize: rect.width,
              blockSize: rect.height
            }]
          };
        }) as ResizeObserverEntry[];

        if (entries.length > 0) {
          this.callback(entries, this as any);
        }
        
        this.rafId = requestAnimationFrame(check);
      };
      
      this.rafId = requestAnimationFrame(check);
    }

    private stopObserving() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }
  }

  (window as any).ResizeObserver = ResizeObserverPolyfill;
}

// localStorage fallback for private browsing
export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('localStorage not available');
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage not available');
    }
  }
};

// sessionStorage fallback
export const safeSessionStorage = {
  getItem: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      console.warn('sessionStorage not available');
    }
  },
  removeItem: (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.warn('sessionStorage not available');
    }
  }
};

// Console polyfill for IE
if (!window.console) {
  window.console = {
    log: () => {},
    warn: () => {},
    error: () => {},
    info: () => {},
    debug: () => {},
    trace: () => {},
    dir: () => {},
    time: () => {},
    timeEnd: () => {},
    group: () => {},
    groupEnd: () => {},
    clear: () => {}
  } as any;
}

// requestAnimationFrame polyfill
if (!window.requestAnimationFrame) {
  window.requestAnimationFrame = function(callback) {
    return window.setTimeout(callback, 1000 / 60);
  };
}

if (!window.cancelAnimationFrame) {
  window.cancelAnimationFrame = function(id) {
    clearTimeout(id);
  };
}

export default {};